import Table from '@/components/admin/Table';
import Alert from '@/components/ui/Alert';
import Button from '@/components/ui/Button';
import {
  getBankAccount,
  getWalletData,
  getWithdrawals,
  requestWithdrawal,
  WalletBankAccountResponse,
  WalletDataResponse,
  WithdrawalResponse,
  getKYCStatus,
  submitKYC,
  KYCStatusResponse,
} from '@/services/walletService';
import { invoiceService, TransactionApiResponse } from '@/services/invoiceService';
import { Icon } from '@iconify/react';
import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Pagination from '@/components/admin/Pagination';
import { Link } from 'react-router-dom';
import FileUpload from '@/components/forms/FileUpload';
import authService from '@/services/authService';

// Helpers
const maskAccountNumber = (acc?: string) => {
  if (!acc) return '-';
  const clean = acc.replace(/\s+/g, '');
  if (clean.length <= 6) return clean;
  const first = clean.slice(0, 4);
  const last = clean.slice(-4);
  return `${first} • • • • ${last}`;
};

const getBankBadge = (bankName?: string, bankCode?: string) => {
  const code = (bankCode || bankName || 'BANK').toUpperCase();
  if (code.includes('MANDIRI') || code === 'MDR') return { text: 'MDR', bg: 'bg-[#004B93]' };
  if (code.includes('BCA')) return { text: 'BCA', bg: 'bg-[#00529C]' };
  if (code.includes('BNI')) return { text: 'BNI', bg: 'bg-[#E35205]' };
  if (code.includes('BRI')) return { text: 'BRI', bg: 'bg-[#00529C]' };
  if (code.includes('BSI')) return { text: 'BSI', bg: 'bg-[#00A39D]' };
  if (code.includes('JAGO')) return { text: 'JAGO', bg: 'bg-[#FF5500]' };
  if (code.includes('CIMB')) return { text: 'CIMB', bg: 'bg-[#ED1B24]' };
  return { text: code.slice(0, 4), bg: 'bg-[#0E1726]' };
};

const formatDateIndo = (dateStr?: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const RiwayatMobileSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => (
  <div className="divide-y divide-gray-100 animate-pulse">
    {Array.from({ length: count }).map((_, idx) => (
      <div key={idx} className="py-3.5 first:pt-1 last:pb-1 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-2xl bg-gray-100 shrink-0" />
          <div className="space-y-2 min-w-0">
            <div className="h-3.5 w-32 sm:w-44 bg-gray-200 rounded-md" />
            <div className="h-2.5 w-24 bg-gray-100 rounded-md" />
          </div>
        </div>
        <div className="space-y-2 text-right shrink-0">
          <div className="h-3.5 w-20 bg-gray-200 rounded-md ml-auto" />
          <div className="h-4 w-12 bg-gray-100 rounded-full ml-auto" />
        </div>
      </div>
    ))}
  </div>
);

const CommunityWalletSkeleton: React.FC = () => {
  return (
    <div className="bg-[#F8FAFC] min-h-screen text-[#0E1726] pb-6 md:pb-6 animate-pulse">
      <div className="max-w-2xl md:max-w-6xl mx-auto px-4 md:px-6 pt-4 md:pt-6 space-y-4 md:space-y-6">
        {/* Top Section Grid: Dark Card & Rekening Card */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {/* 1. TOP DARK NAVY CARD SKELETON */}
          <div className="md:col-span-7 bg-[#0F1E38] rounded-3xl p-5 sm:p-6 border border-slate-800 space-y-4 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-2">
                <div className="h-3.5 w-28 bg-slate-700/60 rounded-md" />
                <div className="h-8 w-44 bg-slate-700/80 rounded-xl" />
              </div>
              <div className="h-8 w-16 bg-slate-700/50 rounded-full" />
            </div>

            {/* Sub-balances breakdown */}
            <div className="grid grid-cols-2 gap-4 pt-3 border-t border-white/10">
              <div className="space-y-1.5">
                <div className="h-3 w-20 bg-slate-700/50 rounded" />
                <div className="h-5 w-28 bg-slate-700/70 rounded-md" />
                <div className="h-2.5 w-24 bg-slate-700/40 rounded" />
              </div>
              <div className="space-y-1.5">
                <div className="h-3 w-20 bg-slate-700/50 rounded" />
                <div className="h-5 w-28 bg-slate-700/70 rounded-md" />
                <div className="h-2.5 w-24 bg-slate-700/40 rounded" />
              </div>
            </div>

            {/* Tarik Saldo Button */}
            <div className="w-full h-12 rounded-2xl bg-slate-700/70" />
          </div>

          {/* 2. REKENING PENCAIRAN CARD SKELETON */}
          <div className="md:col-span-5 bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4 flex flex-col justify-between">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="h-4 w-32 bg-gray-200 rounded-md" />
                <div className="h-7 w-16 bg-orange-100/60 rounded-full" />
              </div>

              {/* Bank Box */}
              <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-10 rounded-xl bg-gray-200" />
                  <div className="space-y-1.5">
                    <div className="h-4 w-32 bg-gray-200 rounded-md" />
                    <div className="h-3 w-24 bg-gray-100 rounded-md" />
                  </div>
                </div>
                <div className="w-5 h-5 rounded-full bg-emerald-100" />
              </div>
            </div>

            {/* Bottom Notice */}
            <div className="h-3 w-56 bg-gray-100 rounded-md" />
          </div>
        </div>

        {/* 3. RIWAYAT PENDAPATAN SKELETON */}
        <div className="space-y-3">
          <div className="space-y-1 px-1">
            <div className="h-5 w-40 bg-gray-200 rounded-md" />
            <div className="h-3.5 w-60 bg-gray-100 rounded-md" />
          </div>

          <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
            {/* Mobile items */}
            <div className="md:hidden">
              <RiwayatMobileSkeleton count={3} />
            </div>

            {/* Desktop Table rows */}
            <div className="hidden md:block space-y-3">
              <div className="h-10 bg-gray-100/70 rounded-xl" />
              {Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} className="h-12 bg-gray-50/80 rounded-xl" />
              ))}
            </div>
          </div>
        </div>

        {/* 4. RIWAYAT PENGELUARAN SKELETON */}
        <div className="space-y-3">
          <div className="space-y-1 px-1">
            <div className="h-5 w-40 bg-gray-200 rounded-md" />
            <div className="h-3.5 w-60 bg-gray-100 rounded-md" />
          </div>

          <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
            {/* Mobile items */}
            <div className="md:hidden">
              <RiwayatMobileSkeleton count={3} />
            </div>

            {/* Desktop Table rows */}
            <div className="hidden md:block space-y-3">
              <div className="h-10 bg-gray-100/70 rounded-xl" />
              {Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} className="h-12 bg-gray-50/80 rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CommunityWallet: React.FC = () => {
  // Core Data States
  const [walletData, setWalletData] = useState<WalletDataResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [withdrawalData, setWithdrawalData] = useState<WithdrawalResponse | null>(null);
  const [bankAccountData, setBankAccountData] = useState<WalletBankAccountResponse | null>(null);
  const [transactionData, setTransactionData] = useState<TransactionApiResponse | null>(null);
  const [transactionPage, setTransactionPage] = useState<number>(1);
  const [transactionLimit] = useState<number>(10);

  // Modal States
  const [withdrawModalOpen, setWithdrawModalOpen] = useState<boolean>(false);
  const [withdrawAmountInput, setWithdrawAmountInput] = useState<number>(0);
  const [isCustomAmount, setIsCustomAmount] = useState<boolean>(false);
  const [customAmountError, setCustomAmountError] = useState<string | null>(null);
  const [isSubmittingWithdraw, setIsSubmittingWithdraw] = useState<boolean>(false);

  // Success Modal State (Image 3)
  const [successModalOpen, setSuccessModalOpen] = useState<boolean>(false);
  const [successWithdrawAmount, setSuccessWithdrawAmount] = useState<number>(0);

  // Sort states
  const [transactionSortBy, setTransactionSortBy] = useState<string>('date');
  const [transactionSortOrder, setTransactionSortOrder] = useState<'asc' | 'desc'>('desc');
  const [withdrawalSortBy, setWithdrawalSortBy] = useState<string>('date');
  const [withdrawalSortOrder, setWithdrawalSortOrder] = useState<'asc' | 'desc'>('desc');

  // KYC States
  const [kycLoading, setKycLoading] = useState<boolean>(true);
  const [kycResponse, setKycResponse] = useState<KYCStatusResponse | null>(null);
  const [showFormOverride, setShowFormOverride] = useState<boolean>(false);
  const [kycSubmitError, setKycSubmitError] = useState<string | null>(null);

  const userRole = authService.getRole() || 'community';

  // Fetch all wallet data
  const fetchWalletData = async (page: number = 1) => {
    setLoading(true);
    setError(null);
    setIsError(false);

    try {
      // 1. Fetch KYC Status first
      const kycStatusResult = await getKYCStatus();
      setKycResponse(kycStatusResult);
      setKycLoading(false);

      if (kycStatusResult.error) {
        setIsError(true);
        setError(`KYC Status: ${kycStatusResult.error}`);
        setLoading(false);
        return;
      }

      // If they don't have a wallet, stop fetching further wallet data
      if (!kycStatusResult.has_wallet) {
        setLoading(false);
        return;
      }

      const errors: string[] = [];
      const result = await getWalletData();
      setWalletData(result);

      if (result.error) {
        errors.push(`Wallet Data: ${result.error}`);
      }

      // Fetch Bank Account
      try {
        const bankResult = await getBankAccount();
        if (!bankResult.error) {
          setBankAccountData(bankResult);
        }
      } catch (bankErr) {
        console.error('Failed to load bank account:', bankErr);
      }

      // Fetch Withdrawals
      const withdrawalResult = await getWithdrawals(withdrawalSortBy, withdrawalSortOrder);
      if (withdrawalResult.error) {
        errors.push(`Withdrawals: ${withdrawalResult.error}`);
      } else {
        setWithdrawalData(withdrawalResult);
      }

      // Fetch online transactions
      const role = authService.getRole();
      if (role === 'community') {
        try {
          const transactionResult = await invoiceService.getArenaOnlineTransactions(
            page,
            transactionLimit,
            transactionSortBy,
            transactionSortOrder
          );
          setTransactionData(transactionResult);
        } catch (transactionError: any) {
          errors.push(`Transactions: ${transactionError.message}`);
        }
      } else {
        errors.push('Community role not matches');
      }

      if (errors.length > 0) {
        setIsError(true);
        setError(errors.join(' | '));
      }
    } catch (err) {
      console.error('Unexpected error in fetchWalletData:', err);
      setIsError(true);
      setError('An unexpected error occurred while loading wallet data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWalletData(transactionPage);
  }, [
    transactionPage,
    transactionSortBy,
    transactionSortOrder,
    withdrawalSortBy,
    withdrawalSortOrder,
  ]);

  const handleTransactionPageChange = (page: number) => {
    setTransactionPage(page);
  };

  const handleTransactionSort = (key: string) => {
    if (transactionSortBy === key) {
      setTransactionSortOrder(transactionSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setTransactionSortBy(key);
      setTransactionSortOrder('desc');
    }
    setTransactionPage(1);
  };

  const handleWithdrawalSort = (key: string) => {
    if (withdrawalSortBy === key) {
      setWithdrawalSortOrder(withdrawalSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setWithdrawalSortBy(key);
      setWithdrawalSortOrder('desc');
    }
  };

  // Open Withdraw Modal (Image 2)
  const handleOpenWithdraw = async () => {
    // Refresh bank account
    if (!bankAccountData) {
      const result = await getBankAccount();
      setBankAccountData(result);
    }
    const balance = walletData?.balance || 0;
    setWithdrawAmountInput(balance);
    setIsCustomAmount(false);
    setCustomAmountError(null);
    setWithdrawModalOpen(true);
  };

  // Execute Withdrawal
  const handleConfirmWithdraw = async () => {
    const adminFee = walletData?.withdraw_fee ?? 2775;
    const balance = walletData?.balance || 0;
    const amount = withdrawAmountInput;

    if (amount < 13000) {
      setCustomAmountError('Minimal penarikan adalah Rp 13.000');
      return;
    }
    if (amount > balance) {
      setCustomAmountError(`Maksimal penarikan adalah Rp ${balance.toLocaleString('id-ID')}`);
      return;
    }

    setIsSubmittingWithdraw(true);
    setCustomAmountError(null);

    try {
      // Backend expects net amount
      const netAmount = Math.max(0, amount - adminFee);
      const result = await requestWithdrawal(netAmount);

      if (result.error) {
        setError(result.error);
        setIsError(true);
      } else {
        setWithdrawModalOpen(false);
        setSuccessWithdrawAmount(netAmount);
        setSuccessModalOpen(true);

        // Refresh wallet
        fetchWalletData(transactionPage);
        const withdrawalResult = await getWithdrawals();
        setWithdrawalData(withdrawalResult);
      }
    } catch (err: any) {
      setError(err?.message || 'Gagal memproses penarikan dana');
      setIsError(true);
    } finally {
      setIsSubmittingWithdraw(false);
    }
  };

  if (kycLoading || (loading && !walletData && kycResponse?.has_wallet !== false)) {
    return <CommunityWalletSkeleton />;
  }

  // Render KYC Form
  const renderKYCForm = () => {
    const prefill = kycResponse?.prefill || { full_name: '', phone_number: '', email: '' };
    const prevReq = kycResponse?.kyc_request;

    const initialKYCValues = {
      full_name: prevReq?.full_name || prefill.full_name || '',
      phone_number: prevReq?.phone_number || prefill.phone_number || '',
      email: prevReq?.email || prefill.email || '',
      id_card_number: prevReq?.id_card_number || '',
      bank_name: prevReq?.bank_name || '',
      account_number: prevReq?.account_number || '',
      account_holder_name: prevReq?.account_holder_name || '',
      address: prevReq?.address || '',
      npwp: prevReq?.npwp || '',
      id_card_photo: null as File | null,
      selfie_photo: null as File | null,
    };

    const kycValidationSchema = Yup.object({
      full_name: Yup.string().required('Nama Lengkap wajib diisi'),
      phone_number: Yup.string().required('Nomor HP wajib diisi'),
      email: Yup.string().email('Format email tidak valid').required('Email wajib diisi'),
      id_card_number: Yup.string()
        .matches(/^\d{16}$/, 'Nomor KTP harus terdiri dari 16 digit angka')
        .required('Nomor KTP wajib diisi'),
      bank_name: Yup.string().required('Nama Bank wajib diisi'),
      account_number: Yup.string().required('Nomor Rekening wajib diisi'),
      account_holder_name: Yup.string().required('Nama Pemilik Rekening wajib diisi'),
      address: Yup.string().required('Alamat Lengkap wajib diisi'),
      npwp: Yup.string().nullable(),
      id_card_photo: Yup.mixed().required('Foto KTP wajib diunggah'),
      selfie_photo: Yup.mixed().required('Foto Selfie dengan KTP wajib diunggah'),
    });

    const handleKYCSubmit = async (values: typeof initialKYCValues, { setSubmitting }: any) => {
      if (!values.id_card_photo || !values.selfie_photo) {
        return;
      }

      setKycSubmitError(null);

      const res = await submitKYC({
        full_name: values.full_name,
        phone_number: values.phone_number,
        email: values.email,
        id_card_number: values.id_card_number,
        bank_name: values.bank_name,
        account_number: values.account_number,
        account_holder_name: values.account_holder_name,
        address: values.address,
        npwp: values.npwp || undefined,
        id_card_photo: values.id_card_photo,
        selfie_photo: values.selfie_photo,
      });

      if (res.error) {
        setKycSubmitError(res.error);
        setSubmitting(false);
      } else {
        setShowFormOverride(false);
        setKycLoading(true);
        fetchWalletData();
      }
    };

    const commonBanks = [
      { code: 'BCA', name: 'Bank Central Asia (BCA)' },
      { code: 'MANDIRI', name: 'Bank Mandiri' },
      { code: 'BNI', name: 'Bank Negara Indonesia (BNI)' },
      { code: 'BRI', name: 'Bank Rakyat Indonesia (BRI)' },
      { code: 'CIMB', name: 'CIMB Niaga' },
      { code: 'PERMATA', name: 'Bank Permata' },
      { code: 'DANAMON', name: 'Bank Danamon' },
      { code: 'BSI', name: 'Bank Syariah Indonesia (BSI)' },
      { code: 'JAGO', name: 'Bank Jago' },
    ];

    return (
      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-[#E0E6ED] shadow-2xs text-[#0E1726]">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center text-[#FC480D]">
            <Icon icon="solar:user-speak-bold-duotone" className="w-7 h-7" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Verifikasi KYC Wallet</h2>
            <p className="text-gray-500 text-xs">
              Lengkapi formulir identitas dan rekening untuk mengaktifkan Wallet SPORTO Anda.
            </p>
          </div>
        </div>

        <Formik
          initialValues={initialKYCValues}
          validationSchema={kycValidationSchema}
          onSubmit={handleKYCSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-6">
              {kycSubmitError && (
                <Alert
                  type="danger"
                  title="Gagal Mengirim KYC"
                  body={kycSubmitError}
                  isDismissable
                />
              )}

              {/* SECTION 1: INFORMASI IDENTITAS */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-100 pb-2">
                  1. Informasi Identitas Penanggungjawab
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Nama Lengkap (Sesuai KTP)
                    </label>
                    <Field
                      name="full_name"
                      type="text"
                      className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FC480D]"
                      placeholder="Nama lengkap"
                    />
                    <ErrorMessage
                      name="full_name"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Nomor HP
                    </label>
                    <Field
                      name="phone_number"
                      type="text"
                      className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FC480D]"
                      placeholder="Nomor HP"
                    />
                    <ErrorMessage
                      name="phone_number"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                    <Field
                      name="email"
                      type="email"
                      className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FC480D]"
                      placeholder="Alamat email"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Nomor NIK KTP
                    </label>
                    <Field
                      name="id_card_number"
                      type="text"
                      className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FC480D]"
                      placeholder="Masukkan 16 digit NIK KTP"
                    />
                    <ErrorMessage
                      name="id_card_number"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* SECTION 2: INFORMASI REKENING */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-100 pb-2">
                  2. Informasi Rekening Bank
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Nama Bank
                    </label>
                    <Field
                      name="bank_name"
                      as="select"
                      className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FC480D] bg-white"
                    >
                      <option value="">Pilih Bank</option>
                      {commonBanks.map((b) => (
                        <option key={b.code} value={b.code}>
                          {b.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="bank_name"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Nomor Rekening
                    </label>
                    <Field
                      name="account_number"
                      type="text"
                      className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FC480D]"
                      placeholder="Masukkan nomor rekening"
                    />
                    <ErrorMessage
                      name="account_number"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Nama Pemilik Rekening
                    </label>
                    <Field
                      name="account_holder_name"
                      type="text"
                      className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FC480D]"
                      placeholder="Masukkan nama pemilik rekening (harus sesuai buku tabungan)"
                    />
                    <ErrorMessage
                      name="account_holder_name"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* SECTION 3: DOKUMEN PENDUKUNG */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-100 pb-2">
                  3. Dokumen Pendukung
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <FileUpload name="id_card_photo" label="Foto KTP" accept="image/*" required />
                  </div>

                  <div>
                    <FileUpload
                      name="selfie_photo"
                      label="Foto Selfie dengan KTP"
                      accept="image/*"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* SECTION 4: INFORMASI TAMBAHAN */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-100 pb-2">
                  4. Informasi Tambahan
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Alamat Lengkap (Sesuai KTP)
                    </label>
                    <Field
                      name="address"
                      as="textarea"
                      rows={3}
                      className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FC480D]"
                      placeholder="Masukkan alamat lengkap sesuai KTP"
                    />
                    <ErrorMessage
                      name="address"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Nomor NPWP (Opsional)
                    </label>
                    <Field
                      name="npwp"
                      type="text"
                      className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FC480D]"
                      placeholder="Masukkan nomor NPWP jika ada"
                    />
                    <ErrorMessage
                      name="npwp"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
                <Button
                  type="button"
                  variant="outline"
                  className="px-6 py-2.5 rounded-xl font-semibold"
                  onClick={() => setShowFormOverride(false)}
                >
                  Batal
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="px-8 py-2.5 rounded-xl font-semibold shadow bg-[#FC480D] hover:bg-[#D83B08] text-white"
                  loading={isSubmitting}
                >
                  Ajukan Verifikasi
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  };

  // Calculations for balance & withdraw
  const adminFee = walletData?.withdraw_fee ?? 2775;
  const balance = walletData?.balance || 0;
  const pendingBalance = walletData?.saldo_pending || 0;
  const totalInWallet = balance + pendingBalance;
  const totalReceived = Math.max(0, withdrawAmountInput - adminFee);
  const bankBadgeInfo = getBankBadge(bankAccountData?.bank_name, bankAccountData?.bank_code);

  // Transactions & Withdrawals filter
  const transactionsList = transactionData?.data || [];
  const withdrawalsList = withdrawalData?.withdrawals || [];
  const incomeTransactions = transactionsList.filter((t) => t.reference_type !== 'withdrawal');

  const transactionColumns = [
    { key: 'date', title: 'Tanggal', width: 'w-36', sortable: true },
    { key: 'description', title: 'Keterangan', width: 'w-72' },
    { key: 'kind', title: 'Jenis Transaksi', width: 'w-36', sortable: true },
    { key: 'payment_type', title: 'Tipe', width: 'w-28', align: 'center' as const, headerAlign: 'center' as const },
    { key: 'amount', title: 'Jumlah', width: 'w-36', align: 'right' as const, headerAlign: 'right' as const, sortable: true },
    { key: 'status', title: 'Status', width: 'w-28', align: 'center' as const, headerAlign: 'center' as const, sortable: true },
  ];

  const transactionTableData = incomeTransactions.map((t) => {
    let kindDisplay = t.reference_type || '';
    switch (kindDisplay) {
      case 'booking':
        kindDisplay = 'Mabar';
        break;
      case 'withdrawal':
        kindDisplay = 'Penarikan';
        break;
      case 'refund':
        kindDisplay = 'Refund';
        break;
      case 'adjustment':
        kindDisplay = 'Penyesuaian';
        break;
      default:
        kindDisplay = 'Online';
    }

    return {
      date: formatDateIndo(t.created_at),
      description: t.description || 'Transaksi Mabar',
      kind: kindDisplay,
      payment_type: 'Online',
      amount: `Rp ${t.amount.toLocaleString('id-ID')}`,
      status: t.status,
      raw: t,
    };
  });

  const withdrawalColumns = [
    { key: 'date', title: 'Tanggal', width: 'w-36', sortable: true },
    { key: 'withdrawal_code', title: 'Kode Penarikan', width: 'w-40', sortable: true },
    { key: 'nominal', title: 'Nominal', width: 'w-36', align: 'right' as const, headerAlign: 'right' as const, sortable: true },
    { key: 'destination', title: 'Tujuan Rekening', width: 'w-64', sortable: true },
    { key: 'status', title: 'Status', width: 'w-28', align: 'center' as const, headerAlign: 'center' as const, sortable: true },
  ];

  const withdrawalTableData = withdrawalsList.map((w) => ({
    date: formatDateIndo(w.requested_at),
    withdrawal_code: w.withdrawal_code,
    nominal: `Rp ${w.amount.toLocaleString('id-ID')}`,
    destination: `${w.bank_name} - ${w.account_number} a/n ${w.account_holder_name}`,
    status: w.status,
    raw: w,
  }));

  const renderTransactionStatusCell = (status: string) => {
    const s = (status || '').toUpperCase();
    if (s === 'SUCCESSFUL') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-600 border border-emerald-100">
          Selesai
        </span>
      );
    }
    if (s === 'PENDING') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-600 border border-amber-100">
          Pending
        </span>
      );
    }
    return (
      <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-600 border border-rose-100">
        {status}
      </span>
    );
  };

  const renderWithdrawalStatusCell = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s === 'completed' || s === 'paid') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-600 border border-emerald-100">
          Selesai
        </span>
      );
    }
    if (s === 'pending' || s === 'processing') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-600 border border-amber-100">
          Diproses
        </span>
      );
    }
    return (
      <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-600 border border-rose-100">
        {s === 'failed' ? 'Gagal' : 'Dibatalkan'}
      </span>
    );
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen text-[#0E1726] pb-6 md:pb-6">
      {/* Alert Notices */}
      {isError && error && (
        <div className="max-w-6xl mx-auto px-4 pt-4">
          <Alert type="danger" title="Error Loading Wallet Data" body={error} isDismissable />
        </div>
      )}

      {/* If community & KYC not approved */}
      {userRole === 'community' && kycResponse?.kyc_status !== 'approved' ? (
        <div className="max-w-2xl mx-auto px-4 pt-6 space-y-6">
          {showFormOverride ? (
            renderKYCForm()
          ) : (
            <div className="bg-white rounded-3xl p-8 border border-[#E0E6ED] text-center space-y-6 shadow-2xs">
              {kycResponse?.kyc_status === 'not_applied' && (
                <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center text-[#FC480D] mx-auto">
                  <Icon icon="solar:wallet-bold-duotone" className="w-12 h-12" />
                </div>
              )}
              {kycResponse?.kyc_status === 'pending' && (
                <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center text-amber-500 mx-auto">
                  <Icon
                    icon="solar:clock-circle-bold-duotone"
                    className="w-12 h-12 animate-pulse"
                  />
                </div>
              )}
              {kycResponse?.kyc_status === 'rejected' && (
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center text-red-500 mx-auto">
                  <Icon icon="solar:danger-circle-bold-duotone" className="w-12 h-12" />
                </div>
              )}

              <div className="inline-flex items-center">
                {kycResponse?.kyc_status === 'not_applied' && (
                  <span className="bg-gray-100 text-gray-800 border border-gray-200 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider">
                    Belum Mengajukan
                  </span>
                )}
                {kycResponse?.kyc_status === 'pending' && (
                  <span className="bg-amber-100 text-amber-800 border border-amber-200 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider animate-pulse">
                    Menunggu Persetujuan
                  </span>
                )}
                {kycResponse?.kyc_status === 'rejected' && (
                  <span className="bg-red-100 text-red-800 border border-red-200 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider">
                    Ditolak
                  </span>
                )}
              </div>

              <div className="space-y-2">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                  {kycResponse?.kyc_status === 'not_applied' && 'Aktifkan Wallet Komunitas Anda'}
                  {kycResponse?.kyc_status === 'pending' && 'Verifikasi KYC Sedang Ditinjau'}
                  {kycResponse?.kyc_status === 'rejected' && 'Verifikasi KYC Ditolak'}
                </h2>
                <p className="text-gray-500 text-xs sm:text-sm leading-relaxed max-w-md mx-auto">
                  {kycResponse?.kyc_status === 'not_applied' &&
                    'Untuk mulai melakukan Transaksi di SPORTO, mengelola dana dan pendaftaran mabar, serta mencairkan pendapatan komunitas Anda, silakan ajukan verifikasi KYC terlebih dahulu.'}
                  {kycResponse?.kyc_status === 'pending' &&
                    'Permohonan aktivasi Wallet Anda sedang diperiksa oleh tim Admin Sporto. Proses ini memakan waktu maksimal 1x24 jam kerja.'}
                  {kycResponse?.kyc_status === 'rejected' &&
                    'Mohon maaf, permohonan verifikasi KYC Anda belum disetujui karena data atau dokumen yang diunggah kurang sesuai.'}
                </p>
              </div>

              {kycResponse?.kyc_status === 'rejected' && kycResponse.kyc_request && (
                <div className="bg-red-50 text-red-700 p-4 rounded-2xl text-xs sm:text-sm font-medium w-full text-left flex items-start space-x-2 border border-red-200">
                  <Icon icon="solar:info-circle-bold" className="w-5 h-5 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold">Alasan Penolakan:</p>
                    <p className="mt-1 font-normal">
                      {kycResponse.kyc_request.rejection_reason ||
                        'Dokumen KTP atau foto selfie kurang jelas.'}
                    </p>
                  </div>
                </div>
              )}

              <div className="pt-2">
                {kycResponse?.kyc_status === 'not_applied' && (
                  <button
                    type="button"
                    className="px-8 py-3 rounded-2xl font-bold text-white bg-[#FC480D] hover:bg-[#D83B08] shadow-md transition duration-200 cursor-pointer"
                    onClick={() => setShowFormOverride(true)}
                  >
                    Mulai Verifikasi KYC
                  </button>
                )}
                {kycResponse?.kyc_status === 'rejected' && (
                  <button
                    type="button"
                    className="px-8 py-3 rounded-2xl font-bold text-white bg-[#FC480D] hover:bg-[#D83B08] shadow-md transition duration-200 cursor-pointer"
                    onClick={() => setShowFormOverride(true)}
                  >
                    Ajukan Ulang Verifikasi
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Regular Wallet View (Mobile & Desktop) */
        <div className="max-w-2xl md:max-w-6xl mx-auto px-4 md:px-6 pt-4 md:pt-6 space-y-4 md:space-y-6 animate-fade-in">
          {/* Top Section Grid: Dark Card & Rekening Card */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
            {/* 1. TOP DARK NAVY CARD (Image 1) */}
            <div className="md:col-span-7 bg-gradient-to-b from-[#0F1E38] to-[#0A1324] rounded-3xl p-5 sm:p-6 text-white border border-slate-800 shadow-xl space-y-4">
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-400">Total saldo di wallet</p>
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
                  {loading ? (
                    <span className="animate-pulse">Memuat...</span>
                  ) : (
                    `Rp ${totalInWallet.toLocaleString('id-ID')}`
                  )}
                </h2>
              </div>

              {/* Sub-balances breakdown */}
              <div className="grid grid-cols-2 gap-4 pt-3 border-t border-white/10">
                {/* Saldo Pending */}
                <div className="space-y-0.5">
                  <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                    <Icon icon="ph:hourglass-bold" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span>Saldo pending</span>
                  </div>
                  <p className="text-sm sm:text-base font-bold text-white">
                    Rp {pendingBalance.toLocaleString('id-ID')}
                  </p>
                  <p className="text-[10px] text-gray-400 font-normal">Cair H+2 setelah event</p>
                </div>

                {/* Siap Ditarik */}
                <div className="space-y-0.5">
                  <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                    <Icon icon="ph:check-circle-bold" className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    <span>Siap ditarik</span>
                  </div>
                  <p className="text-sm sm:text-base font-bold text-emerald-400">
                    Rp {balance.toLocaleString('id-ID')}
                  </p>
                  <p className="text-[10px] text-gray-400 font-normal">
                    Biaya admin Rp {adminFee.toLocaleString('id-ID')}
                  </p>
                </div>
              </div>

              {/* Action Button: Tarik Saldo */}
              <button
                type="button"
                onClick={handleOpenWithdraw}
                disabled={balance < 13000 || !bankAccountData?.bank_name}
                className="w-full h-12 rounded-2xl bg-[#FC480D] hover:bg-[#D83B08] text-white font-extrabold text-sm flex items-center justify-center gap-2 shadow-lg shadow-orange-500/25 active:scale-[0.99] transition cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Icon icon="ph:arrow-down-bold" className="w-4 h-4" />
                <span>Tarik Saldo</span>
              </button>
            </div>

            {/* 2. REKENING PENCAIRAN CARD (Image 1) */}
            <div className="md:col-span-5 bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-extrabold text-[#0E1726]">Rekening Pencairan</h3>
                  <Link
                    to="/host/profile-settings"
                    className="h-7 px-3 rounded-full bg-orange-50 hover:bg-orange-100 text-[#FC480D] text-xs font-bold flex items-center gap-1 transition-colors"
                  >
                    <Icon icon="ph:pencil-simple-line-bold" className="w-3.5 h-3.5" />
                    <span>Ubah</span>
                  </Link>
                </div>

                {/* Bank Account Info Box */}
                {bankAccountData?.bank_name ? (
                  <div className="p-3.5 rounded-2xl bg-gray-50/80 border border-gray-100 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`w-11 h-10 rounded-xl ${bankBadgeInfo.bg} text-white font-black text-xs tracking-wider flex items-center justify-center shrink-0 shadow-xs`}
                      >
                        {bankBadgeInfo.text}
                      </div>
                      <div className="min-w-0 space-y-0.5">
                        <p className="font-extrabold text-sm text-[#0E1726] tracking-wide truncate">
                          {maskAccountNumber(bankAccountData.account_number)}
                        </p>
                        <p className="text-xs text-gray-400 font-medium truncate">
                          a/n {bankAccountData.account_holder_name}
                        </p>
                      </div>
                    </div>

                    <div className="shrink-0 text-emerald-500">
                      <Icon icon="ph:check-circle-fill" className="w-5 h-5" />
                    </div>
                  </div>
                ) : (
                  <div className="p-3.5 rounded-2xl bg-amber-50/60 border border-amber-200/60 flex items-center justify-between gap-3">
                    <div className="space-y-0.5">
                      <p className="text-xs font-bold text-amber-900">Belum ada rekening</p>
                      <p className="text-[11px] text-amber-700">
                        Tambahkan rekening pencairan di Profile Komunitas.
                      </p>
                    </div>
                    <Link
                      to="/host/profile-settings"
                      className="px-3 py-1.5 rounded-xl bg-white border border-amber-200 text-amber-900 text-xs font-bold shrink-0 hover:bg-amber-100"
                    >
                      Tambah
                    </Link>
                  </div>
                )}
              </div>

              {/* Bottom Notice */}
              <div className="flex items-center gap-1.5 text-[11px] text-gray-400 pt-1">
                <Icon icon="ph:info" className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                <span>Semua pencairan saldo dikirim ke rekening ini.</span>
              </div>
            </div>
          </div>

          {/* 3. RIWAYAT PENDAPATAN SECTION (Tabel 1) */}
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2 px-1">
              <div className="flex items-center gap-2.5">
                <div>
                  <h2 className="text-base sm:text-lg font-extrabold text-[#0E1726]">
                    Riwayat Pendapatan
                  </h2>
                  <p className="text-xs text-gray-400">
                    Transaksi masuk dari event mabar & pemesanan
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
              {/* Mobile Card View (Image 1) */}
              <div className="md:hidden divide-y divide-gray-100">
                {loading ? (
                  <RiwayatMobileSkeleton count={3} />
                ) : incomeTransactions.length === 0 ? (
                  <div className="py-8 text-center space-y-1">
                    <Icon icon="ph:receipt-bold" className="w-7 h-7 text-gray-300 mx-auto" />
                    <p className="text-xs font-bold text-gray-600">Belum Ada Riwayat Pendapatan</p>
                    <p className="text-[11px] text-gray-400">
                      Transaksi mabar dan pendapatan akan muncul di sini.
                    </p>
                  </div>
                ) : (
                  incomeTransactions.map((item, idx) => {
                    const isSuccess = item.status.toUpperCase() === 'SUCCESSFUL';
                    const isPending = item.status.toUpperCase() === 'PENDING';
                    const title =
                      item.description ||
                      (item.reference_type === 'booking' ? 'Mabar Rutin Komunitas' : 'Transaksi Mabar');

                    const playerCount =
                      item.metadata?.players_count ||
                      item.metadata?.total_players ||
                      item.metadata?.player_count;

                    const subtitle = `${formatDateIndo(item.created_at)}${
                      playerCount ? ` · ${playerCount} pemain` : ''
                    }`;

                    return (
                      <div
                        key={item.reference_id || idx}
                        className="py-3.5 first:pt-1 last:pb-1 flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-11 h-11 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                            <Icon icon="ph:arrow-down-left-bold" className="w-5 h-5" />
                          </div>

                          <div className="min-w-0 space-y-0.5">
                            <h4 className="font-extrabold text-xs sm:text-sm text-[#0E1726] truncate">
                              {title}
                            </h4>
                            <p className="text-[11px] text-gray-400 font-medium truncate">
                              {subtitle}
                            </p>
                          </div>
                        </div>

                        <div className="text-right shrink-0 space-y-1">
                          <p className="text-xs sm:text-sm font-extrabold text-emerald-600">
                            + Rp {item.amount.toLocaleString('id-ID')}
                          </p>
                          <div>
                            {isSuccess ? (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-500 uppercase tracking-wider">
                                MASUK
                              </span>
                            ) : isPending ? (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-600 border border-amber-100 uppercase tracking-wider">
                                PENDING
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-600 border border-rose-100 uppercase tracking-wider">
                                {item.status}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block">
                <Table
                  noCardView={true}
                  columns={transactionColumns}
                  data={transactionTableData}
                  onSort={handleTransactionSort}
                  sortKey={transactionSortBy}
                  sortOrder={transactionSortOrder}
                  isLoading={loading}
                  renderCell={(row, key, align) => {
                    if (key === 'status') return renderTransactionStatusCell(row.status);
                    if (key === 'amount')
                      return <span className="font-extrabold text-emerald-600">{row.amount}</span>;
                    return (
                      <div
                        className={
                          align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''
                        }
                      >
                        {row[key]}
                      </div>
                    );
                  }}
                />
              </div>

              {/* Pagination */}
              <div className="pt-2">
                <Pagination
                  total={transactionData?.pagination.total ?? 0}
                  perPage={transactionLimit}
                  current={transactionPage}
                  onChange={(p) => handleTransactionPageChange(p)}
                />
              </div>
            </div>
          </div>

          {/* 4. RIWAYAT PENGELUARAN SECTION (Tabel 2) */}
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2 px-1">
              <div className="flex items-center gap-2.5">
                <div>
                  <h2 className="text-base sm:text-lg font-extrabold text-[#0E1726]">
                    Riwayat Pengeluaran
                  </h2>
                  <p className="text-xs text-gray-400">
                    Pencairan dana ke rekening bank terdaftar
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
              {/* Mobile Card View (Image 1) */}
              <div className="md:hidden divide-y divide-gray-100">
                {loading ? (
                  <RiwayatMobileSkeleton count={3} />
                ) : withdrawalsList.length === 0 ? (
                  <div className="py-8 text-center space-y-1">
                    <Icon icon="ph:receipt-bold" className="w-7 h-7 text-gray-300 mx-auto" />
                    <p className="text-xs font-bold text-gray-600">Belum Ada Riwayat Penarikan</p>
                    <p className="text-[11px] text-gray-400">
                      Anda belum pernah melakukan penarikan saldo.
                    </p>
                  </div>
                ) : (
                  withdrawalsList.map((w, idx) => {
                    const isSuccess =
                      w.status.toLowerCase() === 'completed' || w.status.toLowerCase() === 'paid';
                    const isPending =
                      w.status.toLowerCase() === 'pending' ||
                      w.status.toLowerCase() === 'processing';

                    return (
                      <div
                        key={w.withdrawal_code || idx}
                        className="py-3.5 first:pt-1 last:pb-1 flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-11 h-11 rounded-2xl bg-gray-100 border border-gray-200/80 flex items-center justify-center text-gray-600 shrink-0">
                            <Icon icon="ph:arrow-up-right-bold" className="w-5 h-5" />
                          </div>

                          <div className="min-w-0 space-y-0.5">
                            <h4 className="font-extrabold text-xs sm:text-sm text-[#0E1726] truncate">
                              Pencairan ke {w.bank_name} {w.account_number?.slice(-4) || ''}
                            </h4>
                            <p className="text-[11px] text-gray-400 font-medium">
                              {formatDateIndo(w.requested_at)} · 1×24 jam kerja
                            </p>
                          </div>
                        </div>

                        <div className="text-right shrink-0 space-y-1">
                          <p className="text-xs sm:text-sm font-extrabold text-[#0E1726]">
                            - Rp {w.amount.toLocaleString('id-ID')}
                          </p>
                          <div>
                            {isSuccess ? (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-500 uppercase tracking-wider">
                                SELESAI
                              </span>
                            ) : isPending ? (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-600 border border-amber-100 uppercase tracking-wider">
                                DIPROSES
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-600 border border-rose-100 uppercase tracking-wider">
                                GAGAL
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block">
                <Table
                  noCardView={true}
                  columns={withdrawalColumns}
                  data={withdrawalTableData}
                  onSort={handleWithdrawalSort}
                  sortKey={withdrawalSortBy}
                  sortOrder={withdrawalSortOrder}
                  isLoading={loading}
                  renderCell={(row, key, align) => {
                    if (key === 'status') return renderWithdrawalStatusCell(row.status);
                    if (key === 'nominal')
                      return <span className="font-extrabold text-[#0E1726]">{row.nominal}</span>;
                    return (
                      <div
                        className={
                          align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''
                        }
                      >
                        {row[key]}
                      </div>
                    );
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= TARIK SALDO MODAL / BOTTOM SHEET (Image 2) ================= */}
      {withdrawModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-[#0E1726]/60 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200"
          onClick={() => setWithdrawModalOpen(false)}
        >
          <div
            className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 flex flex-col gap-4 shadow-2xl animate-in slide-in-from-bottom duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile Drag Indicator */}
            <div className="w-12 h-1 rounded-full bg-gray-300 self-center -mt-1 sm:hidden" />

            {/* Header with Icon, Title, and Close Button */}
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-2xl bg-orange-50 border border-orange-100 flex items-center justify-center text-[#FC480D] shrink-0">
                  <Icon icon="ph:arrow-down-bold" className="w-5 h-5" />
                </div>
                <div className="space-y-0.5">
                  <h3 className="text-base sm:text-lg font-extrabold text-[#0E1726]">
                    Tarik Saldo?
                  </h3>
                  <p className="text-xs text-gray-400 font-medium leading-relaxed">
                    Dana dikirim ke rekening yang terdaftar di Profile komunitas.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setWithdrawModalOpen(false)}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition cursor-pointer"
              >
                <Icon icon="ph:x-bold" className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Bank Preview Box */}
            <div className="p-3.5 rounded-2xl bg-gray-50/80 border border-gray-200/70 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className={`w-11 h-10 rounded-xl ${bankBadgeInfo.bg} text-white font-black text-xs tracking-wider flex items-center justify-center shrink-0 shadow-xs`}
                >
                  {bankBadgeInfo.text}
                </div>
                <div className="min-w-0 space-y-0.5">
                  <p className="font-extrabold text-sm text-[#0E1726] tracking-wide truncate">
                    {maskAccountNumber(bankAccountData?.account_number)}
                  </p>
                  <p className="text-xs text-gray-400 font-medium truncate">
                    a/n {bankAccountData?.account_holder_name}
                  </p>
                </div>
              </div>

              <div className="shrink-0 text-emerald-500">
                <Icon icon="ph:check-circle-fill" className="w-5 h-5" />
              </div>
            </div>

            {/* Calculation Breakdown Card */}
            <div className="p-4 rounded-2xl bg-gray-50/50 border border-gray-200/70 space-y-2.5">
              <div className="flex items-center justify-between text-xs font-medium text-gray-500">
                <span>Saldo siap ditarik</span>
                <span className="font-extrabold text-[#0E1726]">
                  Rp {withdrawAmountInput.toLocaleString('id-ID')}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs font-medium text-gray-500">
                <span>Biaya admin</span>
                <span className="font-extrabold text-gray-700">
                  - Rp {adminFee.toLocaleString('id-ID')}
                </span>
              </div>

              <hr className="border-gray-200" />

              <div className="flex items-center justify-between pt-0.5">
                <span className="text-xs sm:text-sm font-extrabold text-[#0E1726]">
                  Total diterima
                </span>
                <span className="text-base sm:text-lg font-black text-[#FC480D]">
                  Rp {totalReceived.toLocaleString('id-ID')}
                </span>
              </div>

              {/* Optional custom amount toggle */}
              <div className="pt-1">
                {!isCustomAmount ? (
                  <button
                    type="button"
                    onClick={() => setIsCustomAmount(true)}
                    className="text-[11px] font-bold text-[#FC480D] hover:underline cursor-pointer"
                  >
                    Tarik sebagian saldo saja?
                  </button>
                ) : (
                  <div className="pt-2 space-y-1">
                    <label className="text-[11px] font-bold text-gray-600 block">
                      Nominal Penarikan
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-xs font-bold text-gray-400">
                        Rp
                      </span>
                      <input
                        type="number"
                        value={withdrawAmountInput || ''}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          setWithdrawAmountInput(val);
                        }}
                        max={balance}
                        min={13000}
                        placeholder="Masukkan nominal"
                        className="w-full h-10 pl-9 pr-3 rounded-xl border border-gray-300 text-xs font-bold text-[#0E1726] focus:outline-none focus:border-[#FC480D]"
                      />
                    </div>
                  </div>
                )}
                {customAmountError && (
                  <p className="text-[11px] font-bold text-rose-500 mt-1">{customAmountError}</p>
                )}
              </div>
            </div>

            {/* Note */}
            <div className="flex items-start gap-1.5 text-[11px] text-gray-400 leading-relaxed">
              <Icon icon="ph:info" className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400" />
              <span>
                Dana masuk dalam 1×24 jam kerja. Ubah rekening di Profile → Rekening Pencairan.
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={() => setWithdrawModalOpen(false)}
                className="flex-1 h-12 rounded-full border border-gray-200 text-xs sm:text-sm font-bold text-gray-600 hover:bg-gray-50 transition cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleConfirmWithdraw}
                disabled={isSubmittingWithdraw || withdrawAmountInput < 13000}
                className="flex-1 h-12 rounded-full bg-[#FC480D] hover:bg-[#D83B08] text-white text-xs sm:text-sm font-bold flex items-center justify-center gap-1.5 shadow-md shadow-orange-500/20 active:scale-98 transition cursor-pointer disabled:opacity-60"
              >
                {isSubmittingWithdraw ? (
                  <>
                    <Icon icon="ph:spinner-bold" className="w-4 h-4 animate-spin" />
                    <span>Memproses...</span>
                  </>
                ) : (
                  <span>Tarik</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= PENCAIRAN DIPROSES SUCCESS MODAL (Image 3) ================= */}
      {successModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-[#0E1726]/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setSuccessModalOpen(false)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-3xl p-6 sm:p-7 flex flex-col items-center text-center gap-4 shadow-2xl animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Orange Checkmark Circle */}
            <div className="w-16 h-16 rounded-full bg-orange-50 border border-orange-100 flex items-center justify-center text-[#FC480D] shadow-xs">
              <Icon icon="ph:check-circle-bold" className="w-9 h-9" />
            </div>

            {/* Title & Description */}
            <div className="space-y-2">
              <h3 className="text-lg font-extrabold text-[#0E1726]">Pencairan Diproses</h3>
              <p className="text-xs text-gray-500 font-medium leading-relaxed">
                Rp {successWithdrawAmount.toLocaleString('id-ID')} dikirim ke{' '}
                {bankAccountData?.bank_name} {maskAccountNumber(bankAccountData?.account_number)} a/n{' '}
                {bankAccountData?.account_holder_name} dalam 1×24 jam kerja.
              </p>
            </div>

            {/* Full-width Button */}
            <button
              type="button"
              onClick={() => setSuccessModalOpen(false)}
              className="w-full h-12 rounded-full bg-[#FC480D] hover:bg-[#D83B08] text-white font-extrabold text-sm shadow-md shadow-orange-500/25 active:scale-98 transition cursor-pointer mt-2"
            >
              Mengerti
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityWallet;
